

import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaVideo, FaBuilding, FaCommentDots } from "react-icons/fa";
import api from '../api/axios'; // adjust to match your existing api client import path
import Navbar from "./Navbar";
import EmployerSidebar, { ChevronDownIcon } from "./EmployerSidebar";
import ScheduleInterviewModal from "./ScheduleInterviewModal";
import InterviewFeedbackModal from "./InterviewFeedbackModal";

const COLORS = {
  blue: "#9333EA",
  orange: "#F59E0B",
  dark: "#1E1033",
};

const STATUS_STYLES = {
  Scheduled: { bg: "#F3E8FF", text: COLORS.blue },
  Completed: { bg: "#F0FDF4", text: "#16A34A" },
  Rescheduled: { bg: "#FFFBEB", text: COLORS.orange },
  Cancelled: { bg: "#FEF2F2", text: "#DC2626" },
};

const RECOMMENDATION_STYLES = {
  proceed: { bg: "#F0FDF4", text: "#16A34A", label: "Proceed" },
  hold: { bg: "#FFFBEB", text: COLORS.orange, label: "Hold" },
  reject: { bg: "#FEF2F2", text: "#DC2626", label: "Reject" },
};

export default function EmployerInterviews() {
  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });
  const navigate = useNavigate();

  const [interviews, setInterviews] = useState([]);
  const [jobPostings, setJobPostings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [feedbackTarget, setFeedbackTarget] = useState(null);
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);

  const loadInterviews = () => {
    setLoading(true);
    api
      .get("/employer/interviews")
      .then((res) => setInterviews(res.data))
      .catch(() => setError("Couldn't load interviews."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    loadInterviews();
    api
      .get("/jobs")
      .then((res) => setJobPostings(res.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (statusFilter === "all") return interviews;
    return interviews.filter((i) => i.status === statusFilter);
  }, [interviews, statusFilter]);

  const formatDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      day: "numeric",
      month: "short",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF5FF] to-[#FDF2F8]">
      <Navbar user={user} />

      <div className="flex">
        <EmployerSidebar user={user} activeTab="reports" />

        <div className="flex-1 min-w-0 lg:ml-16 lg:peer-hover:ml-64 transition-all duration-200 ease-in-out">
      <div className="mx-auto max-w-5xl px-4 py-8">
        {/* Mobile-only: nav drawer */}
        <button
          onClick={() => setShowFiltersMobile((s) => !s)}
          className="lg:hidden w-full mb-4 bg-white rounded-xl shadow p-4 flex items-center justify-between font-semibold text-[#1E1033]"
        >
          <span>☰ Menu</span>
          <span className="text-sm text-gray-400 inline-flex items-center gap-1">
            {showFiltersMobile ? 'Hide' : 'Show'}
            <ChevronDownIcon className={`transition-transform ${showFiltersMobile ? 'rotate-180' : ''}`} />
          </span>
        </button>

        {showFiltersMobile && <EmployerSidebar user={user} activeTab="reports" mobile />}

        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-[#7C3AED] via-[#C026D3] to-[#EC4899] bg-clip-text text-transparent inline-block">
              Interviews
            </h1>
            <p className="text-sm text-gray-500">Schedule rounds and record feedback.</p>
          </div>
          <button
            onClick={() => setShowScheduleModal(true)}
            className="flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-[#7C3AED] to-[#DB2777] hover:opacity-90"
          >
            <FaPlus /> Schedule Interview
          </button>
        </div>

        <div className="mb-4 flex gap-2">
          {["all", "Scheduled", "Completed", "Rescheduled", "Cancelled"].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`rounded-full px-3 py-1 text-sm font-medium ${
                statusFilter === s
                  ? "text-white bg-gradient-to-r from-[#7C3AED] to-[#DB2777]"
                  : "text-gray-600 bg-gray-100"
              }`}
            >
              {s === "all" ? "All" : s}
            </button>
          ))}
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>
        )}

        {loading ? (
          <div className="py-16 text-center text-gray-400">Loading interviews...</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 py-16 text-center text-gray-400">
            No interviews {statusFilter !== "all" ? `with status "${statusFilter}"` : "scheduled yet"}.
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3">Candidate</th>
                  <th className="px-4 py-3">Job</th>
                  <th className="px-4 py-3">Round</th>
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3">Mode</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Recommendation</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((interview) => {
                  const statusStyle = STATUS_STYLES[interview.status] || STATUS_STYLES.Scheduled;
                  const recStyle = RECOMMENDATION_STYLES[interview.recommendation];
                  return (
                    <tr key={interview.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium" style={{ color: COLORS.dark }}>
                        {interview.candidate_name}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{interview.job_title || "—"}</td>
                      <td className="px-4 py-3 text-gray-600">{interview.type}</td>
                      <td className="px-4 py-3 text-gray-600">{formatDate(interview.scheduled_at)}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {interview.mode === "online" ? (
                          <FaVideo title="Online" />
                        ) : interview.mode === "offline" ? (
                          <FaBuilding title="Offline" />
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className="rounded-full px-2 py-1 text-xs font-medium"
                          style={{ backgroundColor: statusStyle.bg, color: statusStyle.text }}
                        >
                          {interview.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {recStyle ? (
                          <span
                            className="rounded-full px-2 py-1 text-xs font-medium"
                            style={{ backgroundColor: recStyle.bg, color: recStyle.text }}
                          >
                            {recStyle.label}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => setFeedbackTarget(interview)}
                          className="flex items-center gap-1 text-sm font-medium"
                          style={{ color: COLORS.blue }}
                        >
                          <FaCommentDots /> Feedback
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {showScheduleModal && (
          <ScheduleInterviewModal
            jobPostings={jobPostings}
            onClose={() => setShowScheduleModal(false)}
            onScheduled={loadInterviews}
          />
        )}

        {feedbackTarget && (
          <InterviewFeedbackModal
            interview={feedbackTarget}
            onClose={() => setFeedbackTarget(null)}
            onSaved={loadInterviews}
          />
        )}
      </div>
        </div>
      </div>
    </div>
  );
}